import { Button, message, Segmented, Spin, Switch, Typography } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  balanceFetchListInvoices,
  balanceFetchListOrders,
  balanceFetchListReplenishments,
  createOrderInvoice,
  payInvoice,
} from '../../Reducers/balance';
import { dateRangePeriods, momentToUnixRange } from '../../Utils/date';
import Filters from './Filters';
import Table from './Table';
import { useOutletContext } from 'react-router-dom';
import { CreditCardOutlined } from '@ant-design/icons';
import Page404 from '../Page404';
import { fetchSelectedCustomers } from '../../Reducers/core';
import { useLocation } from 'react-router-dom';
import ReplenishmentTable from './ReplenishmentTable';

const thisWeek = dateRangePeriods()['This Week'];

/** This is Page */
function Balance() {
  const isManager = useOutletContext();
  const [isLoading, setIsLoading] = useState(false);
  const [transactionsData, setTransactionsData] = useState("list_orders");
  const [filters, setFilters] = useState({
    dates: momentToUnixRange([thisWeek[0], thisWeek[1]]),
  });
  const location = useLocation();

  const [newInvoiceId, setNewInvoiceId] = useState(null);

  const payload = useSelector(
    (state) => state.core.customer.id,
    shallowEqual
  );

  const dispatch = useDispatch();

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const type = query.get("type");
    const highlightId = query.get("highlight");

    if (type) {
      setTransactionsData(type);
    }

    if (highlightId) {
      setNewInvoiceId(Number(highlightId));
      setTimeout(() => setNewInvoiceId(null), 5000);
    }
  }, [location.search]);

  const handleFetch = () => {
    if (transactionsData === 'list_invoices') {
      setSelectedIds([])
      dispatch(balanceFetchListInvoices(filters.dates)).catch((error) => {
        if (error.includes('403 Forbidden')) {
          return message.error('Status 403 Forbidden');
        }
        message.error(error.message || error);
      });
    } else if (transactionsData === 'list_orders') {
      dispatch(balanceFetchListOrders(filters.dates)).catch((error) => {
        if (error.includes('403 Forbidden')) {
          return message.error('Status 403 Forbidden');
        }
        message.error(error.message || error);
      });
    } else if (transactionsData === 'list_replenishments') {
      dispatch(balanceFetchListReplenishments(filters.dates)).catch((error) => {
        if (error.includes('403 Forbidden')) {
          return message.error('Status 403 Forbidden');
        }
        message.error(error.message || error);
      });
    } 
    // ⚡ для list_replenishments пока ничего не дергаем
  };

  useEffect(() => {
    handleFetch();
  }, [payload, transactionsData]);

  useEffect(() => {
    if (filters.dates.includes(null)) {
      message.info('Please select the dates!');
      return;
    }
    handleFetch();
  }, [filters]); 

  const [selectedRow, setSelectedRow] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedIdsToAmount, setSelectedIdsToAmount] = useState({});

  const onSelectChange = (newSelectedRowKeys) => {
    if (transactionsData === "list_invoices") {
      if (newSelectedRowKeys.length > 0) {
        const lastKey = newSelectedRowKeys[newSelectedRowKeys.length - 1];
        const [id, amount, status] = lastKey.split("_");
  
        if (status === "1") {
          setSelectedIdsToAmount([{ invoiceId: Number(id), amount: Number(amount) }]);
          setSelectedRow([lastKey]);
        } else {
          setSelectedIdsToAmount([]);
          setSelectedRow([]);
        }
      } else {
        setSelectedIdsToAmount([]);
        setSelectedRow([]);
      }
    } else {
      const orderedKeys = newSelectedRowKeys.filter((key) => {
        const [id, status] = key.split("_")
        return status === "1" || status === "9"
      });

      const ids = orderedKeys.map((key) => {
        const [id] = key.split("_")
        return Number(id)
      })

      setSelectedRow(orderedKeys);
      setSelectedIds(ids);
    }
  };

  const handleSendInvoice = () => {
    dispatch(createOrderInvoice(selectedIds))
    .then(() => {
      message.success(`Successfully created an invoice!`)
      handleFetch();
    })
    .catch((err) => {
      message.error(`Failed to create an invoice! ${err.message}`)
    })
  }

  const emptySelectedIds = !selectedIds.length
  const disabledBgColor =  emptySelectedIds ? "rgba(0, 0, 0, 0.04)" : "#4ec21b"
  const disabledColor =  emptySelectedIds ? "rgba(0, 0, 0, 0.25)" : "white"

  const emptySelectedIdsToAmount = !selectedIdsToAmount.length
  const disabledToPayBgColor =  emptySelectedIdsToAmount ? "rgba(0, 0, 0, 0.04)" : "#4ec21b"
  const disabledToPayColor =  emptySelectedIdsToAmount ? "rgba(0, 0, 0, 0.25)" : "white"

  const handleChange = (val) => {
    setIsLoading(true);
    setTransactionsData(val);
    handleFetch();
    setIsLoading(false);
  };

  const handlePayInvoice = () => {
    dispatch(payInvoice(selectedIdsToAmount))
      .then(() => {
        message.success("Invoice paid");
        dispatch(fetchSelectedCustomers());
        handleFetch();
      })
      .catch((err) => {
        message.error(`Failed to set invoice paid: ${err}`)
      })
  }

  return (
    isManager ? <div>
      <Typography.Title>Transactions</Typography.Title>
      {isManager && <div className='active_filter'>
        <Segmented
          options={[
            { label: 'Orders', value: 'list_orders' },
            { label: 'Invoices', value: 'list_invoices' }
          ]}
          value={transactionsData}
          onChange={handleChange}
        />
      </div>}
      <Filters
        className="mb"
        defaultFilters={filters}
        setFilters={setFilters}
      />

      {isManager && transactionsData === "list_invoices" && (
        <Button 
          disabled={emptySelectedIdsToAmount} 
          onClick={handlePayInvoice} 
          style={{marginBottom: '8px', backgroundColor: disabledToPayBgColor, color: disabledToPayColor}}
        >
          Mark paid <CreditCardOutlined />
        </Button>
      )}

      {!transactionsData ? <Spin /> : (
        <>
          {transactionsData === "list_invoices" || transactionsData === "list_orders" ? (
            <Table 
              isManager={isManager}
              transactionsData={transactionsData}
              selected={selectedRow}
              onSelectChange={onSelectChange}
              handleFetch={handleFetch}
              newInvoiceId={newInvoiceId}
            />
          ) : transactionsData === "list_replenishments" ? (
            <ReplenishmentTable transactionsData={transactionsData} />
          ) : null}
        </>
      )}
    </div> : <Page404 />
  );
}

export default Balance;
